import { lazy, Suspense } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { AppShell } from './AppShell';
import { LoadingState } from '@/components/LoadingState';
import { RequireAuth } from '@/features/auth/RequireAuth';
import { NewAssessmentPage } from '@/features/setup/NewAssessmentPage';
import { QuestionnairePage } from '@/features/questionnaire/QuestionnairePage';
import { SelfServiceQuestionnairePage } from '@/features/selfservice/SelfServiceQuestionnairePage';

/**
 * The sales flow is loaded eagerly: it runs on a stand, where a lazy chunk arriving late is a
 * pause in a conversation. The report and the admin view are split out — a customer opening
 * their link on a phone has no reason to download the administration table, and the report is
 * the one page that has to load fast on someone else's network.
 */
const ResultPage = lazy(() =>
  import('@/features/result/ResultPage').then((module) => ({ default: module.ResultPage })),
);
const ContactPage = lazy(() =>
  import('@/features/contact/ContactPage').then((module) => ({ default: module.ContactPage })),
);
const SentPage = lazy(() =>
  import('@/features/contact/SentPage').then((module) => ({ default: module.SentPage })),
);
const AdminPage = lazy(() =>
  import('@/features/admin/AdminPage').then((module) => ({ default: module.AdminPage })),
);
const ReportPage = lazy(() =>
  import('@/features/report/ReportPage').then((module) => ({ default: module.ReportPage })),
);
const LoginPage = lazy(() =>
  import('@/features/auth/LoginPage').then((module) => ({ default: module.LoginPage })),
);
const SelfServiceStartPage = lazy(() =>
  import('@/features/selfservice/SelfServiceStartPage').then((module) => ({
    default: module.SelfServiceStartPage,
  })),
);
const SelfServiceResultPage = lazy(() =>
  import('@/features/selfservice/SelfServiceResultPage').then((module) => ({
    default: module.SelfServiceResultPage,
  })),
);
const SelfServiceSentPage = lazy(() =>
  import('@/features/selfservice/SelfServiceSentPage').then((module) => ({
    default: module.SelfServiceSentPage,
  })),
);

function deferred(element: React.ReactNode) {
  return <Suspense fallback={<LoadingState />}>{element}</Suspense>;
}

/**
 * The order of the flow is deliberate (specification 3.1): the questions come before the
 * contact details, because you ask for an email address only once you have given something
 * back; and the result appears on screen before sending, because that is where the sales
 * conversation happens. The email is only the receipt.
 */
export const router = createBrowserRouter([
  // The PIN screen sits outside the shell and outside the gate — it is the way through the gate.
  { path: '/login', element: deferred(<LoginPage />) },
  {
    // Everything the sales rep and the administrator see requires a PIN session. The gate wraps
    // the shell rather than each page, so a route added later is protected by default instead of
    // by remembering.
    element: <RequireAuth />,
    children: [
      {
        path: '/',
        element: <AppShell />,
        children: [
          { index: true, element: <NewAssessmentPage /> },
          { path: 'vurdering/:id', element: <QuestionnairePage /> },
          { path: 'vurdering/:id/resultat', element: deferred(<ResultPage />) },
          { path: 'vurdering/:id/kontakt', element: deferred(<ContactPage />) },
          { path: 'vurdering/:id/sendt', element: deferred(<SentPage />) },
          { path: 'admin', element: deferred(<AdminPage />) },
        ],
      },
    ],
  },
  // The public report link. No shell, no login, no cookie (specification 9).
  { path: '/r/:token', element: deferred(<ReportPage />) },
  // The public self-service path: a customer starts and finishes an assessment with no rep and
  // no PIN. Outside RequireAuth for the same reason the report link is — an assessment id here
  // is the access control, exactly like a report token.
  { path: '/selvbetjening', element: deferred(<SelfServiceStartPage />) },
  { path: '/selvbetjening/:id', element: <SelfServiceQuestionnairePage /> },
  { path: '/selvbetjening/:id/resultat', element: deferred(<SelfServiceResultPage />) },
  { path: '/selvbetjening/:id/sendt', element: deferred(<SelfServiceSentPage />) },
]);
